import React, { useMemo, useState, useEffect } from 'react';
import { useEntityRouter } from '../hooks/useEntityRouter';
import { Box, Button, Stack, TextField, IconButton, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPrograms,
  createProgram,
  updateProgram,
  deleteProgram,
  Program,
  assignStoreToProgram,
  removeStoreFromProgram,
  ProgramStore,
  updateProgramStore,
  importProgramStoresExcel,
} from '../services/programService';
import { listPartners, Partner } from '../services/partnerService';
import { listStores, Store, listStoresForProgram } from '../services/storeService';
import DataTable from '../components/DataTable';
import FormDialog from '../components/FormDialog';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import AddStoreToProgramDialog from '../components/AddStoreToProgramDialog';

const schema = yup.object({
  name: yup.string().required(),
  partner_id: yup.number().required(),
});

type FormValues = yup.InferType<typeof schema>;

// Helper function for compact weekly schedule
// function compactWeeklySchedule(visiting_hours: any) {
//   if (!visiting_hours) return '-';
//   if (visiting_hours.simple && typeof visiting_hours.simple.weekly === 'number') {
//     return `Visits per week: ${visiting_hours.simple.weekly}, per month: ${visiting_hours.simple.monthly}`;
//   }
//   if (!visiting_hours.weekly) return '-';
//   const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
//   const keys = ['1', '2', '3', '4', '5', '6', '0'];
//   return days.map((day, i) => {
//     const periods = visiting_hours.weekly[keys[i]] || [];
//     if (!periods.length) return `${day}: -`;
//     return `${day}: ` + periods.map((p: any) => `${p.start}-${p.end}`).join(', ');
//   }).join('; ');
// }

const ProgramsPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { entityId, navigateToEntity, navigateToList } = useEntityRouter('/programs');
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState<Program | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [programStores, setProgramStores] = useState<ProgramStore[]>([]);
  // const [storeAssignForm, setStoreAssignForm] = useState<{ store_id: number; visiting_hours: WeeklyWorkScheduleValue; visit_time_minutes: number }>({ store_id: 0, visiting_hours: { weekly: {}, holidays: [] }, visit_time_minutes: 0 });
  const [_storeAssignError ] = useState<string | null>(null);
  const [addStoreDialogOpen, setAddStoreDialogOpen] = useState(false);
  const [editStoreDialogOpen, setEditStoreDialogOpen] = useState(false);
  const [storeToEdit, setStoreToEdit] = useState<ProgramStore | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<string>('maheev');
  const [importLoading, setImportLoading] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { name: '', partner_id: 0 },
  });

  const { data: programs = [], isLoading } = useQuery({ queryKey: ['programs'], queryFn: listPrograms });

  // Load specific program if entityId is present
  useEffect(() => {
    if (entityId && programs.length > 0) {
      const program = programs.find((p: Program) => p.id === parseInt(entityId));
      if (program) {
        handleEdit(program);
      }
    }
  }, [entityId, programs]);

  useEffect(() => {
    listPartners().then(setPartners);
    listStores().then(setStores);
  }, []);

  useEffect(() => {
    if (editData && editData.id) {
      listStoresForProgram(editData.id).then(setProgramStores);
    } else {
      setProgramStores([]);
    }
  }, [editData]);

  const createMut = useMutation({
    mutationFn: createProgram,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programs'] }),
  });
  const updateMut = useMutation({
    mutationFn: (payload: Program) => updateProgram(payload.id!, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programs'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteProgram(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programs'] }),
  });

  const onSubmit = (vals: FormValues) => {
    if (editData) updateMut.mutate({ ...editData, ...vals });
    else createMut.mutate(vals);
    setOpenForm(false);
    // Navigate back to list view after saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    // Navigate back to list view when closing without saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleAdd = () => {
    reset({ name: '', partner_id: partners[0]?.id || 0 });
    setEditData(null);
    setOpenForm(true);
  };

  const handleEdit = (row: Program) => {
    setEditData(row);
    reset(row as FormValues);
    setOpenForm(true);
  };

  const handleRowClick = (row: Program) => {
    navigateToEntity(row.id!);
  };

  // const handleStoreAssign = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setStoreAssignError(null);
  //   if (!editData?.id) {
  //     setStoreAssignError('Save the program first.');
  //     return;
  //   }
  //   try {
  //     await assignStoreToProgram({
  //       program_id: editData.id,
  //       store_id: storeAssignForm.store_id,
  //       visiting_hours: storeAssignForm.visiting_hours,
  //       visit_time_minutes: storeAssignForm.visit_time_minutes,
  //     });
  //     setStoreAssignForm({ store_id: 0, visiting_hours: { weekly: {}, holidays: [] }, visit_time_minutes: 0 });
  //     listStoresForProgram(editData.id).then(setProgramStores);
  //   } catch (err) {
  //     setStoreAssignError('Failed to assign store.');
  //   }
  // };

  const handleRemoveStore = async (id: number) => {
    if (!editData?.id) return;
    await removeStoreFromProgram(id);
    listStoresForProgram(editData.id).then(setProgramStores);
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: t('programs.name'), flex: 1 },
      { field: 'partner', headerName: t('programs.partner'), width: 120, valueGetter: (params: any) => {
        if (!params || !params.name) return '-';
        return params.name;
      } },
      {
        field: 'actions',
        headerName: t('programs.actions'),
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteId(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [partners, t]
  );

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>{t('programs.title')}</h2>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          {t('programs.add')}
        </Button>
      </Stack>

      <DataTable 
        rows={programs} 
        columns={columns} 
        loading={isLoading} 
        onRowDoubleClick={handleEdit}
        onRowClick={handleRowClick}
      />

      <FormDialog open={openForm} title={editData ? t('programs.edit') : t('programs.add')} onClose={handleCloseForm} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} pt={1}>
          <Controller
            name="name"
            control={control}
            // render={({ field }) => (
            //   <TextField label="Name" {...field} fullWidth _error={!!errors.name} helperText={errors.name?.message} />
            // )}
            render={({ field }) => (
              <TextField label={t('programs.name')} {...field} fullWidth helperText={errors.name?.message} />
            )}
          />
          <Controller
            name="partner_id"
            control={control}
            render={({ field }) => (
              //   _error={!!errors.partner_id}
              <TextField select label={t('programs.partner')} {...field} fullWidth  helperText={errors.partner_id?.message}>
                {partners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
        {/* Store assignment UI */}
        {(editData && editData.id) && (
          <>
            <Button variant="outlined" startIcon={<AddIcon />} sx={{ mb: 2 }} onClick={() => setAddStoreDialogOpen(true)}>
              {t('programs.addStore')}
            </Button>
            {/* Import from Excel UI */}
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <TextField
                select
                label={t('programs.importType')}
                value={importType}
                onChange={e => setImportType(e.target.value)}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="maheev">maheev</MenuItem>
                {/* Add more types as needed */}
              </TextField>
              <Button variant="contained" component="label" size="small">
                {t('programs.selectFile')}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={e => {
                    if (e.target.files && e.target.files[0]) setImportFile(e.target.files[0]);
                  }}
                />
              </Button>
              <span>{importFile?.name || ''}</span>
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={!importFile || !importType || importLoading}
                onClick={async () => {
                  if (!editData?.id || !importFile) return;
                  setImportLoading(true);
                  try {
                    await importProgramStoresExcel(editData.id, importType, importFile);
                    listStoresForProgram(editData.id).then(setProgramStores);
                    alert(t('programs.importSuccessful'));
                    setImportFile(null);
                  } catch (e: any) {
                    alert(t('programs.importFailed') + ': ' + (e?.response?.data?.detail || e.message));
                  } finally {
                    setImportLoading(false);
                  }
                }}
              >
                {importLoading ? t('programs.importing') : t('programs.import')}
              </Button>
            </Stack>
            <DataTable
              rows={programStores}
              columns={[
                { field: 'store_id', headerName: t('programs.store'), flex: 1, valueGetter: (params: any) => {
                  const store = stores.find(s => s.id === params);
                  return store ? store.address : params;
                }},
                  
                { field: 'visit_time_minutes', headerName: t('programs.visitTimeMinutes'), flex: 1 },
                { field: 'rate', headerName: t('programs.rate'), flex: 1 },
                {
                  field: 'actions',
                  headerName: t('programs.actions'),
                  width: 160,
                  sortable: false,
                  renderCell: (params: any) => (
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small" onClick={() => {
                        setStoreToEdit(params.row);
                        setEditStoreDialogOpen(true);
                      }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveStore(params.row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ),
                },
              ]}
              loading={false}
            />
            <AddStoreToProgramDialog
              open={addStoreDialogOpen}
              onClose={() => setAddStoreDialogOpen(false)}
              stores={stores}
              chains={[]}
              planningDays={(editData as any)?.yandex_options?.planning_days || 30}
              onAdd={async ({ store_id, visit_time_minutes, visiting_hours, rate }) => {
                if (!editData?.id) return;
                await assignStoreToProgram({
                  program_id: editData.id,
                  store_id,
                  visit_time_minutes,
                  visiting_hours,
                  rate,
                });
                listStoresForProgram(editData.id).then(setProgramStores);
                setAddStoreDialogOpen(false);
              }}
            />
            <AddStoreToProgramDialog
              open={editStoreDialogOpen}
              onClose={() => {
                setEditStoreDialogOpen(false);
                setStoreToEdit(null);
              }}
              stores={stores}
              chains={[]}
              planningDays={(editData as any)?.yandex_options?.planning_days || 30}
              initialValues={storeToEdit ? {
                store_id: storeToEdit.store_id,
                visit_time_minutes: storeToEdit.visit_time_minutes,
                visiting_hours: storeToEdit.visiting_hours,
                rate: storeToEdit.rate,
              } : undefined}
              onAdd={async ({ store_id, visit_time_minutes, visiting_hours, rate }) => {
                if (!editData?.id || !storeToEdit) return;
                await updateProgramStore(storeToEdit.id!, {
                  program_id: editData.id,
                  store_id,
                  visit_time_minutes,
                  visiting_hours,
                  rate,
                });
                listStoresForProgram(editData.id).then(setProgramStores);
                setEditStoreDialogOpen(false);
                setStoreToEdit(null);
              }}
            />
          </>
        )}
      </FormDialog>

      <ConfirmDeleteDialog
        open={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMut.mutate(deleteId);
          setDeleteId(null);
        }}
      />
    </Box>
  );
};

export default ProgramsPage; 